import React, { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  isAuthenticated,
  getCurrentUser,
  authApi,
  ApiError,
} from "../services/api";

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  allowedRoles?: string[];
  redirectTo?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = false,
  allowedRoles = [],
  redirectTo = "/login",
}) => {
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [location.pathname]);

  const checkAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!requireAuth) {
        // No authentication required, allow access
        setIsValid(true);
        return;
      }

      // Check if user is authenticated
      if (!isAuthenticated()) {
        setIsValid(false);
        return;
      }

      // Verify session with server
      const session = await authApi.verifySession();
      const user = getCurrentUser();

      if (!session || !user) {
        setIsValid(false);
        return;
      }

      // Check role permissions if specified
      if (
        allowedRoles.length > 0 &&
        !allowedRoles.includes(user.membershipTier)
      ) {
        setIsValid(false);
        setError("Access denied: Insufficient permissions");
        return;
      }

      setIsValid(true);
    } catch (err) {
      console.error("Auth check failed:", err);

      if (ApiError.isApiError(err) && err.statusCode === 401) {
        setIsValid(false);
      } else {
        setError("Authentication check failed");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !isValid) {
    // Redirect to login or show error
    if (error && error.includes("Insufficient permissions")) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Contact support if you believe this is an error.
            </p>
          </div>
        </div>
      );
    }

    // Redirect to login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!requireAuth && isValid && isAuthenticated()) {
    // If user is authenticated but trying to access public page, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
